/**
 * Голосовой ввод в чате создания песни — чистая логика (без React и DOM), чтобы
 * её можно было прогнать юнит-тестами (`voiceInput.test.ts`).
 *
 * Две стратегии за одной кнопкой микрофона:
 *  - "speech" — Web Speech API (`window.SpeechRecognition`): мгновенный результат
 *    прямо в браузере, без запроса к нашему API. Есть по факту только в
 *    десктопном Chrome;
 *  - "record" — запись MediaRecorder + серверная расшифровка
 *    (`POST /voice/transcribe`, Whisper — тот же движок, которым боты
 *    превращают голосовые в текст). Работает в webview Telegram/MAX и в iOS
 *    Safari, где SpeechRecognition отсутствует;
 *  - "none" — недоступны оба пути, кнопку микрофона не показываем.
 */
/**
 * Ограничение длительности записи, мс. 60 с ≈ 1 МБ opus/webm — с запасом
 * укладываемся в лимит ручки (10 MB) и не даём человеку надиктовать монолог,
 * который потом долго расшифровывается.
 */
export const VOICE_MAX_DURATION_MS = 60000;
/**
 * Web Speech API — быстрее и дешевле, поэтому он в приоритете; запись с
 * отправкой на сервер берём только когда его нет.
 */
export function resolveVoiceInputStrategy(opts) {
    if (opts.hasSpeechRecognition)
        return "speech";
    if (opts.canRecord)
        return "record";
    return "none";
}
/**
 * Feature-detect Web Speech API. `win` — точка подмены для тестов; в приложении
 * вызывается без аргументов (и безопасен там, где window нет вовсе).
 */
export function hasSpeechRecognitionApi(win) {
    const w = win ??
        (typeof window !== "undefined"
            ? window
            : null);
    return !!(w && (w.SpeechRecognition || w.webkitSpeechRecognition));
}
/**
 * Дописывает распознанное к уже введённому тексту (а не затирает его) — так же,
 * как это делает путь Web Speech API. Пустое распознавание поле не трогает.
 */
export function appendTranscript(base, transcript) {
    const tail = transcript.trim();
    if (!tail)
        return base;
    const head = base.trim();
    return head ? `${head} ${tail}` : tail;
}
/** Таймер записи — всегда mm:ss с ведущими нулями (00:00, 00:07…). */
export function formatVoiceTimer(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
/**
 * Разбор тела ответа `POST /voice/transcribe` → `{"text": "..."}`.
 * `text` МОЖЕТ быть пустым (тишина в записи) — это не ошибка, а отдельный
 * сценарий: вызывающий показывает «ничего не расслышали», а не пустоту молча.
 * Всё, что не строка (битое тело), приводим к пустой строке.
 */
export function parseTranscribeResponse(data) {
    const text = data?.text;
    return typeof text === "string" ? text : "";
}
const readStatus = (error) => {
    const status = error?.response
        ?.status;
    return typeof status === "number" ? status : null;
};
const readDetail = (error) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === "string" ? detail.trim() : "";
};
/**
 * Сообщение об ошибке расшифровки — по кодам контракта ручки.
 *
 * Особый случай — 429: он приходит и на «слишком часто» (20/мин), и на
 * исчерпанную гостевую квоту, а различаются они ТОЛЬКО текстом `detail`.
 * Поэтому здесь (как в resolveTextActionErrorMessage) показываем сам `detail`,
 * если он есть, и только на пустом откатываемся к общей формулировке.
 */
export function resolveTranscribeErrorMessage(error, language) {
    const ru = language === "ru";
    switch (readStatus(error)) {
        case 400:
            return ru
                ? "Запись не подошла. Попробуйте записать ещё раз или напишите текстом."
                : "The recording didn't work out. Try recording again or type your message.";
        case 401:
            return ru
                ? "Сессия истекла. Обновите страницу и попробуйте снова."
                : "Your session has expired. Reload the page and try again.";
        case 413:
            return ru
                ? "Запись слишком длинная. Запишите покороче — до минуты."
                : "The recording is too long. Keep it under a minute.";
        case 429:
            return (readDetail(error) ||
                (ru
                    ? "Слишком часто. Подождите немного и попробуйте снова."
                    : "Too many requests. Wait a moment and try again."));
        case 502:
        case 503:
            return ru
                ? "Распознавание сейчас недоступно. Напишите сообщение текстом."
                : "Speech recognition is unavailable right now. Please type your message.";
        default:
            return ru
                ? "Не удалось распознать голос. Попробуйте ещё раз или напишите текстом."
                : "Could not recognize your voice. Try again or type your message.";
    }
}
/** Расшифровка вернула пустой текст — в записи не нашлось речи. */
export function emptyTranscriptMessage(language) {
    return language === "ru"
        ? "Ничего не расслышали. Попробуйте записать ещё раз поближе к микрофону."
        : "We didn't catch anything. Try recording again closer to the microphone.";
}
/**
 * Подсказки по состоянию рекордера (`useAudioRecorder`): отказ в доступе к
 * микрофону и прочие ошибки захвата. Показываются прямо над строкой ввода —
 * состояние держится, пока человек не попробует снова, поэтому не тост.
 */
export function micDeniedMessage(language) {
    return language === "ru"
        ? "Нет доступа к микрофону. Разрешите его в настройках и попробуйте снова."
        : "No microphone access. Allow it in the settings and try again.";
}
/** Ошибка захвата звука (не отказ в доступе). */
export function micErrorMessage(language) {
    return language === "ru"
        ? "Не удалось записать голос. Попробуйте ещё раз или напишите текстом."
        : "Could not record your voice. Try again or type your message.";
}
